// src/components/Layout.js
import React, { useEffect, useRef, useState, createContext, useContext } from "react";
import { Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import {
  Bell,
  BookOpen,
  Building2,
  CalendarDays,
  ClipboardList,
  House,
  Languages,
  LogOut,
  Network,
  Power,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  SquareCheckBig,
  Stethoscope,
  Store,
  UserCheck,
  Users,
  Wrench,
} from "lucide-react";
import "./Layout.css";
import { useAuth } from "../contexts/AuthContext";
import { useRBAC } from "../contexts/RBACContext";
// usePermission removed - Layout uses useRBAC directly (Story 8.2)
import { useCoinBalance } from "../contexts/CoinBalanceContext";
import CartIcon from "./shop/CartIcon";
import FloatingDeliveriesButton from "./shop/FloatingDeliveriesButton";
import StudentLayout from "./student/StudentLayout"; // Sprint 2 Fix: Unify Student Navigation
import StudentShopLayout from "./student/learningzone/StudentShopLayout";
import AdminTopMenu from "./AdminTopMenu";
import RoleSwitcher from "./RoleSwitcher";
import {
  getUserNotifications,
  getUnreadNotificationCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  updateNotificationLastViewed,
  getPendingPurchaseRequestCount,  // Story 3.9: PM badge
} from "../api";

// Roles that see the notification bell (students get coins/cart too; admin gets bell only).
const NOTIFICATION_BELL_ROLES = ["student", "admin"];
const canUseNotificationBell = () =>
  NOTIFICATION_BELL_ROLES.includes(localStorage.getItem("role"));

// Admin header (see .admin-layout-header in Layout.css): each top-menu link
// gets an icon before its label. Links without an entry show the label only.
const ADMIN_MENU_ICONS = {
  "/dashboard": House,
  "/daily-schedule": CalendarDays,
  "/admin/daily-schedule": CalendarDays,
  "/users": Users,
  "/machines": Network,
  "/task": SquareCheckBig,
  "/attendance": UserCheck,
  "/balagruha": Building2,
  "/admin/courses": BookOpen,
  "/rbac": ShieldCheck,
  "/repair": Wrench,
  "/purchase": ShoppingCart,
  "/admin/purchase": ShoppingBag,
  "/shop": Store,
  "/wtf": Power,
  "/admin/translations": Languages,
  "/admin/courses/assignments": ClipboardList,
  "/medical/doctors": Stethoscope,
};

// Admin top menu: only these links, in this order (other roles keep the
// topMenus order). Pages left out stay routed and reachable as before.
//   label        replaces the topMenus name
//   alsoActiveOn other pages that highlight the item
//   disabled     shown greyed out, not clickable
const ADMIN_MENU = [
  { link: "/dashboard" },
  { link: "/daily-schedule" },
  { link: "/task" },
  { link: "/wtf" },
  { link: "/admin/courses" },
  { link: "/admin/purchase" },
  { link: "/users" },
  { link: "/rbac" },
  { link: "/medical/doctors" },
  // Machines live under Balgruha (its "Add Machines" button opens /machines).
  { link: "/balagruha", label: "Balgruha", alsoActiveOn: ["/machines"] },
  // Greyed out for now; the page itself is still reachable from Courses.
  { link: "/admin/courses/assignments", disabled: true },
];

// Create Sidebar Context
const SidebarContext = createContext();

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    return { isSidebarCollapsed: false, toggleSidebar: () => { } };
  }
  return context;
};

const Layout = () => {
  const { logout, isAuthenticated, isLoading: authLoading } = useAuth();
  const { isLoading: rbacLoading, hasPermission, permissions } = useRBAC();
  // canRead removed - was destructured from usePermission but never used (Story 8.2)
  const { balance: coinBalance } = useCoinBalance(); // Sprint5-Story-08: Use context for coin balance
  const navigate = useNavigate();
  const location = useLocation(); // Get current location
  const topMenuRef = useRef(null);

  // The top menu scrolls sideways when it doesn't fit; keep the current page's button in view.
  useEffect(() => {
    const active = topMenuRef.current?.querySelector(".menu-item.active");
    if (active) active.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [location.pathname]);
  const [visibleMenus, setVisibleMenus] = useState([]);
  const [role, setRole] = useState("");
  const [notifications, setNotifications] = useState(0);
  const [showChatWindow, setShowChatWindow] = useState(null); // null, "coach", or "admin"
  const [showNotifications, setShowNotifications] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [notificationsList, setNotificationsList] = useState([]);
  // Trigger a brief shake animation on the WTF menu item in child view
  const [shouldShakeWtf, setShouldShakeWtf] = useState(false);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [activeDashboardTab, setActiveDashboardTab] = useState("dashboard");
  // Story 3.9: PM pending badge state
  const [pendingPurchaseCount, setPendingPurchaseCount] = useState({ total: 0, highPriority: 0 });

  // Check if current route is WTF
  const isWTFRoute = location.pathname === "/wtf";
  // Admins keep the top menu on every WTF view (the Management Dashboard and the
  // Wall of Fame board at ?view=wall); every other role's WTF view stays header-less.
  const isWtfAdminView =
    isWTFRoute &&
    (localStorage.getItem("role") || "").toLowerCase() === "admin";
  const isShopRoute = location.pathname.startsWith("/shop");
  const isLowStockReportRoute = location.pathname === "/shop/admin/inventory/low-stock";
  // Doctor Bank renders its own header/back-button — the shared header would
  // just duplicate that (and its unscoped CSS collides with other pages'
  // fixed headers), so it opts out the same way the coach dashboard does.
  const isDoctorBankRoute = location.pathname === "/medical/doctor-bank";

  const topMenus = [
    {
      id: 1,
      name: "Dashboard",
      link: "/dashboard",
      roles: [
        "admin",
        "coach",
        "balagruha-incharge",
        "student",
        "purchase-manager",
        "medical-incharge",
        "sports-coach",
        "music-coach",
        "amma",
      ],
    },
    {
      id: 20,
      name: "Daily Schedule",
      link: "/daily-schedule",
      roles: ["admin"],
    },
    { id: 4, name: "Tasks", link: "/task", roles: ["admin", "coach"] },
    {
      id: 13,
      name: "WTF",
      link: "/wtf",
      roles: [
        "admin",
        "coach",
        "balagruha-incharge",
        "student",
        "medical-incharge",
        "sports-coach",
        "music-coach",
        "amma",
      ],
    },
    { id: 7, name: "Courses", link: "/admin/courses", roles: ["admin"] },
    { id: 2, name: "Users", link: "/users", roles: ["admin", "coach"] },
    {
      id: 3,
      name: "Machines",
      link: "/machines",
      // Admins no longer get a Machines tab in the header; /machines itself
      // is still routed and reachable for them.
      roles: [
        "coach",
        "balagruha-incharge",
        "purchase-manager",
        "medical-incharge",
        "sports-coach",
        "music-coach",
        "amma",
      ],
    },
    {
      id: 5,
      name: "Attendance",
      link: "/attendance",
      roles: ["admin", "coach"],
    },
    // Admin Purchase page: the purchase manager's Purchase page + Purchase
    // Approvals (pages/admin/AdminPurchasePage.jsx). "Purchases" below is unchanged.
    { id: 21, name: "Purchase", link: "/admin/purchase", roles: ["admin"] },
    { id: 6, name: "Balagruhas", link: "/balagruha", roles: ["admin"] },
    { id: 8, name: "Access", link: "/rbac", roles: ["admin"] },
    {
      id: 9,
      name: "Repairs",
      link: "/repair",
      // FIX-008: All non-student roles can access Repair Management (matches /purchase roles)
      roles: ["admin", "purchase-manager", "coach", "medical-incharge", "balagruha-incharge", "sports-coach", "music-coach", "amma"],
    },
    {
      id: 10,
      name: "Purchases",
      link: "/purchase",
      // Sprint5-Story-24 + S24-BUG-005: All roles except students can access Purchase Management
      roles: ["admin", "purchase-manager", "coach", "medical-incharge", "balagruha-incharge", "sports-coach", "music-coach", "amma"],
    },
    {
      id: 11,
      name: "Shop",
      link: "/shop",
      roles: ["student", "admin", "coach", "medical-incharge", "balagruha-incharge", "sports-coach", "music-coach", "amma"],
    },
    {
      id: 12,
      name: "Low Stock",
      link: "/purchase-manager/low-stock",
      roles: ["purchase-manager"],
    },
    // { id: 14, name: "Quizzes", link: "/admin/quizzes", roles: ["admin"] }, // Moved to Course Management page
    { id: 15, name: "Translations", link: "/admin/translations", roles: ["admin"] },
    // Sprint 2 Story 05: split the old mislabeled "Courses" entry into
    // two - a read-only content browser at /coach/courses and the
    // existing syllabus grading tracker at /coach/grading.
    { id: 16, name: "Courses", link: "/coach/courses", roles: ["coach"] },
    { id: 161, name: "Grading", link: "/coach/grading", roles: ["coach"] },
    { id: 17, name: "Assignments", link: "/coach/assignments", roles: ["coach"] },
    { id: 171, name: "Assignments", link: "/admin/courses/assignments", roles: ["admin"] },
    // Medical: Doctors Data Bank - shared directory managed by medical incharge + admin
    { id: 19, name: "Doctors", link: "/medical/doctors", roles: ["medical-incharge", "admin"] },
    {
      id: 18,
      name: "My Courses",
      link: "/student/dashboard",
      roles: ["student"],
    },
  ];

  const sportCoachMenu = [
    { id: 1, name: "Dashboard", activeTab: "dashboard" },
    { id: 2, name: "Students", activeTab: "students" },
    { id: 3, name: "Training", activeTab: "training" },
    { id: 4, name: "Sports Tasks", activeTab: "tasks" },
    { id: 5, name: "Performance", activeTab: "performance" },
    { id: 6, name: "Reports", activeTab: "reports" },
  ];



  const handleNotificationClick = async () => {
    const newShowState = !showNotifications;
    setShowNotifications(newShowState);

    // If opening notifications, update last viewed time
    if (newShowState && canUseNotificationBell()) {
      try {
        await updateNotificationLastViewed();
        // Refresh the unread count to show only new notifications
        await fetchUnreadCount();
      } catch (error) {
        console.error("Error updating notification last viewed time:", error);
      }
    }
  };

  // Fetch notifications for the user
  const fetchNotifications = async () => {
    if (canUseNotificationBell()) {
      try {
        setIsLoadingNotifications(true);
        const result = await getUserNotifications(20, 0);
        if (result.success) {
          setNotificationsList(result.data);
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        setIsLoadingNotifications(false);
      }
    }
  };

  // Fetch unread notification count
  const fetchUnreadCount = async () => {
    if (canUseNotificationBell()) {
      try {
        const result = await getUnreadNotificationCount();
        if (result.success) {
          setNotifications(result.data.count);
        }
      } catch (error) {
        console.error("Error fetching unread count:", error);
      }
    }
  };

  // Story 3.9: Fetch pending purchase request count for PM badge
  const fetchPendingPurchaseCount = async () => {
    const userRole = localStorage.getItem("role");
    if (userRole === "purchase-manager" || userRole === "admin") {
      try {
        const result = await getPendingPurchaseRequestCount();
        if (result.success) {
          setPendingPurchaseCount(result.data);
        }
      } catch (error) {
        console.error("Error fetching pending purchase count:", error);
      }
    }
  };

  // Mark notification as read
  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId);
      // Refresh notifications and unread count
      await fetchNotifications();
      await fetchUnreadCount();
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  // Handle clicking a notification: mark as read, close the panel, and
  // navigate to the page the notification relates to (if any)
  const handleNotificationItemClick = async (notification) => {
    await handleMarkAsRead(notification._id);
    setShowNotifications(false);
    const actionUrl = notification?.metadata?.actionUrl;
    if (actionUrl) {
      navigate(actionUrl);
    }
  };

  // Handle closing notifications panel and mark all as read
  const handleCloseNotifications = async () => {
    if (canUseNotificationBell()) {
      try {
        // Mark all notifications as read
        await markAllNotificationsAsRead();
        // Refresh the unread count
        await fetchUnreadCount();
      } catch (error) {
        console.error("Error marking all notifications as read:", error);
      }
    }
    // Close the panel
    setShowNotifications(false);
  };

  // Format time for display
  const formatTime = (timestamp) => {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - notificationTime) / (1000 * 60));

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60)
      return `${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""} ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24)
      return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7)
      return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;

    return notificationTime.toLocaleDateString();
  };

  const NotificationsPanel = () => {
    return (
      <div className="notifications-panel">
        <div className="notifications-header">
          <h3>Notifications</h3>
          <button onClick={handleCloseNotifications}>&times;</button>
        </div>
        <div className="notifications-list">
          {isLoadingNotifications ? (
            <div className="notification-item">
              <div className="notification-message">
                Loading notifications...
              </div>
            </div>
          ) : notificationsList.length === 0 ? (
            <div className="notification-item">
              <div className="notification-message">No notifications</div>
            </div>
          ) : (
            notificationsList.map((notification) => (
              <div
                key={notification._id}
                className={`notification-item ${!notification.isRead ? "unread" : ""
                  }`}
                onClick={() => handleNotificationItemClick(notification)}
                style={{ cursor: "pointer" }}
              >
                <div className="notification-title">{notification.title}</div>
                <div className="notification-message">
                  {notification.message}
                </div>
                <div className="notification-time">
                  {formatTime(notification.createdAt)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  const canReadModule = (moduleName) => {
    return hasPermission(moduleName, "Read") || hasPermission(moduleName, "Manage");
  };

  const canAccessCoachMenu = (menu) => {
    const userRole = localStorage.getItem("role") || "guest";

    if (userRole !== "coach") {
      return true;
    }

    const permissionMap = {
      "/users": ["User Management"],
      "/machines": ["Machine Management"],
      "/task": ["Task Management"],
      "/attendance": ["Attendance Management", "User Management"],
      "/repair": ["Purchase Management"],
      "/purchase": ["Purchase Management"],
      "/shop": ["Shop Management"],
      "/wtf": ["WTF Management", "WTF Interaction", "WTF Submission"],
      "/coach/courses": ["LMS Management"],
      "/coach/grading": ["LMS Management"],
      "/coach/assignments": ["LMS Management"],
      "/admin/courses/assignments": ["LMS Management"],
    };

    const requiredModules = permissionMap[menu.link];
    if (!requiredModules) return true;

    return requiredModules.some(canReadModule);
  };
  // Check if we're on the dashboard
  const isDashboard =
    location.pathname === "/dashboard" || location.pathname === "/";

  useEffect(() => {
    // Example: Get role from localStorage or API
    const userRole = localStorage.getItem("role") || "guest";
    setRole(userRole);

    // Filter menus based on user role and coach RBAC permissions.
    // Admins get exactly ADMIN_MENU, in its order.
    const filteredMenus =
      userRole === "admin"
        ? ADMIN_MENU.map(({ link, label, ...options }) => {
            const menu = topMenus.find((item) => item.link === link && item.roles.includes("admin"));
            return menu && { ...menu, ...options, name: label || menu.name };
          }).filter(Boolean)
        : topMenus.filter((menu) =>
            menu.roles.includes(userRole) && canAccessCoachMenu(menu)
          );

    setVisibleMenus(filteredMenus);

    // Fetch notifications for student/admin (coin balance now handled by CoinBalanceContext)
    if (NOTIFICATION_BELL_ROLES.includes(userRole)) {
      fetchNotifications();
      fetchUnreadCount();
    }

    // Story 3.9: Fetch pending count for PM badge
    if (userRole === "purchase-manager" || userRole === "admin") {
      fetchPendingPurchaseCount();
      // Poll every 60 seconds for updates
      const interval = setInterval(fetchPendingPurchaseCount, 60000);
      return () => clearInterval(interval);
    }
  }, [permissions, rbacLoading]);

  // Removed duplicate declaration of sportCoachMenu

  useEffect(() => {
    if (location.pathname !== "/dashboard") return;

    if (location.state?.activeTab) {
      setActiveDashboardTab(location.state.activeTab);
    }
  }, [location.pathname, location.state?.activeTab]);

  // If either auth or RBAC is loading, show loading screen
  if (authLoading || rbacLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading application...</p>
      </div>
    );
  }

  // If not authenticated, just render the outlet (which should be login)
  if (!isAuthenticated) {
    return <Outlet />;
  }

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Handle back button click
  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  // Custom menus for roles with custom dashboards
  const customRoleMenus = {
    "medical-incharge": [
      { id: 1, name: "Dashboard", link: "/dashboard", state: { activeTab: "dashboard" } },
      { id: 2, name: "Students", link: "/dashboard", state: { activeTab: "students" } },
      { id: 3, name: "Check Ins", link: "/dashboard", state: { activeTab: "checkins" } },
      { id: 4, name: "Tasks", link: "/dashboard", state: { activeTab: "tasks" } },
      { id: 6, name: "Purchases", link: "/purchase" },
      { id: 7, name: "Shop", link: "/shop" },
      { id: 8, name: "Doctors", link: "/dashboard", state: { activeTab: "medical" } },
    ],
    "sports-coach": [
      { id: 1, name: "Dashboard", link: "/dashboard" },
      { id: 2, name: "Students", link: "/dashboard", state: { activeTab: "students" } },
      { id: 3, name: "Training", link: "/dashboard", state: { activeTab: "training" } },
      { id: 4, name: "Sports Tasks", link: "/dashboard", state: { activeTab: "tasks" } },
      { id: 5, name: "Performance", link: "/dashboard", state: { activeTab: "performance" } },
      { id: 6, name: "Reports", link: "/dashboard", state: { activeTab: "reports" } },
      { id: 7, name: "Tasks", link: "/task" },
      { id: 8, name: "Machines", link: "/machines" },
      { id: 9, name: "Purchases", link: "/purchase" },
      { id: 10, name: "Shop", link: "/shop" },
    ],
    "music-coach": [
      { id: 1, name: "Dashboard", link: "/dashboard" },
      { id: 2, name: "Students", link: "/dashboard", state: { activeTab: "students" } },
      { id: 3, name: "Training", link: "/dashboard", state: { activeTab: "training" } },
      { id: 4, name: "Music Tasks", link: "/dashboard", state: { activeTab: "tasks" } },
      { id: 5, name: "Performance", link: "/dashboard", state: { activeTab: "performance" } },
      { id: 6, name: "Reports", link: "/dashboard", state: { activeTab: "reports" } },
      { id: 7, name: "Tasks", link: "/task" },
      { id: 8, name: "Machines", link: "/machines" },
      { id: 9, name: "Purchases", link: "/purchase" },
      { id: 10, name: "Shop", link: "/shop" },
    ],
    "amma": [
      { id: 1, name: "Dashboard", link: "/dashboard" },
      { id: 2, name: "Students", link: "/dashboard", state: { activeTab: "students" } },
      { id: 3, name: "Tasks", link: "/task" },
      { id: 4, name: "Machines", link: "/machines" },
      { id: 5, name: "Purchases", link: "/purchase" },
      { id: 6, name: "Shop", link: "/shop" },
    ],
  };

  const currentRole = localStorage.getItem("role");
  const isOnDashboard = location.pathname === "/dashboard";
  const hasCustomDashboard = Object.keys(customRoleMenus).includes(currentRole);
  const useMedicalHeaderStyle = currentRole === "medical-incharge";
  const useAdminHeaderStyle = currentRole === "admin";
  // Coach Dashboard redesign: the coach's own dashboard renders its own
  // greeting/nav inside the page (see coach.js), so the shared header is
  // hidden only for coach + /dashboard. Coaches keep the shared header on
  // every other route (Users, Machines, Tasks, etc.).
  const isCoachDashboard = currentRole === "coach" && isOnDashboard;
  // Same for the Medical Incharge dashboard (see MedicalInchargeDashboard.js),
  // which renders its own sidebar (Check-Ins / Purchases / Medical).
  const isMedicalInchargeDashboard = currentRole === "medical-incharge" && isOnDashboard;
  // Same for the Purchase Manager dashboard (see PurchaseManagerDashboard.js),
  // which is only the coach Purchase panel inside the coach sidebar shell.
  const isPurchaseManagerDashboard = currentRole === "purchase-manager" && isOnDashboard;
  // Wall of Fame (/wtf) is its own full-bleed, immersive page (own
  // header/back button - see WallOfFame.js) for every non-student role;
  // students already get it via StudentLayout below, which never reaches
  // here. The shared header would just duplicate that and doesn't match
  // the Wall of Fame's visual design.


  // Determine which menu to show
  const menuToShow = hasCustomDashboard
    ? customRoleMenus[currentRole]
    : visibleMenus;

  // Show Layout menu for all authenticated roles except custom dashboard roles on /dashboard
  const shouldShowLayoutMenu =
    !isCoachDashboard &&
    !isMedicalInchargeDashboard &&
    !isPurchaseManagerDashboard &&
    !isDoctorBankRoute &&
    (!isWTFRoute || isWtfAdminView) &&
    (
      currentRole === "admin" ||
      currentRole === "coach" ||
      currentRole === "student" ||
      currentRole === "purchase-manager" ||
      currentRole === "balagruha-incharge" ||
      currentRole === "medical-incharge" ||
      (hasCustomDashboard && !isOnDashboard)
    );

  // One entry per top-menu link: classes, click handler and badge. Rendered as
  // the plain row below, or by AdminTopMenu (icons + "More") for admins.
  const topMenuItems = menuToShow.map((menu) => {
    const isDashboardMenu = menu.link === "/dashboard";
    const isOnPage = (link) =>
      location.pathname === link ||
      (link !== "/" && location.pathname.startsWith(`${link}/`));
    const isDirectRoute =
      !isDashboardMenu &&
      (isOnPage(menu.link) || (menu.alsoActiveOn || []).some(isOnPage));
    const isActive = menu.disabled
      ? false
      : isDashboardMenu
      ? isOnDashboard &&
        (menu.state?.activeTab
          ? activeDashboardTab === menu.state.activeTab
          : activeDashboardTab === "dashboard")
      : isDirectRoute;
    const isWtf = menu.name === "WTF";
    // The admin's Purchase item carries the pending badge that "Purchases" has for the purchase manager.
    const isPurchases = menu.name === "Purchases" || menu.link === "/admin/purchase";
    const wtfHighlight =
      isWtf && (isWTFRoute || shouldShakeWtf) && role === "student";
    const classes = [
      "menu-item",
      isActive ? "active" : "",
      wtfHighlight ? "wtf-highlight" : "",
      isPurchases ? "has-pending-badge" : "",
      isWtf && shouldShakeWtf ? "shake" : "",
      menu.disabled ? "is-disabled" : "",
    ]
      .filter(Boolean)
      .join(" ");
    const showPendingBadge =
      isPurchases && (role === "purchase-manager" || role === "admin") && pendingPurchaseCount.total > 0;

    return {
      key: menu.id,
      className: classes,
      isActive,
      icon: ADMIN_MENU_ICONS[menu.link],
      label: menu.name,
      badgeText: showPendingBadge ? String(pendingPurchaseCount.total) : "",
      disabled: Boolean(menu.disabled),
      onClick: () => {
        if (menu.disabled) return;
        if (isWtf && role === "student") {
          // Briefly trigger shake on click
          setShouldShakeWtf(true);
          setTimeout(() => setShouldShakeWtf(false), 700);
        }
        // Navigate with state if provided (for dashboard tabs)
        if (menu.state) {
          setActiveDashboardTab(menu.state.activeTab);
          navigate(menu.link, {
            state: { ...menu.state, navKey: Date.now() },
          });
        } else {
          if (menu.link === "/dashboard") {
            setActiveDashboardTab("dashboard");
          }
          navigate(menu.link);
        }
      },
      // Story 3.9: PM pending badge for Purchases menu
      badge: showPendingBadge ? (
        <span
          className="pm-pending-badge"
          title={`${pendingPurchaseCount.total} pending requests${pendingPurchaseCount.highPriority > 0 ? ` (${pendingPurchaseCount.highPriority} high priority)` : ''}`}
        >
          {pendingPurchaseCount.total}
          {pendingPurchaseCount.highPriority > 0 && <span className="high-priority-dot">!</span>}
        </span>
      ) : null,
    };
  });

  // Sprint 2 Fix: Unify Student Navigation
  // If user is a student, use the StudentLayout (with TitleBar) instead of the legacy header
  // This ensures Shop, WTF, and other generic pages look consistent with the Dashboard
  if (currentRole === "student") {
    // The Shop (and its Transactions tab) opens inside the Learning Zone's top bar + sidebar instead.
    if (isShopRoute || location.pathname === "/coins/history") {
      return <StudentShopLayout />;
    }
    return (
      <StudentLayout>
        <SidebarContext.Provider
          value={{
            isSidebarCollapsed,
            toggleSidebar: () => setIsSidebarCollapsed(!isSidebarCollapsed),
          }}
        >
          <Outlet />
        </SidebarContext.Provider>
      </StudentLayout>
    );
  }

  return (
    <div className="app-layout">
      {shouldShowLayoutMenu && (
        <header className={`header ${useMedicalHeaderStyle ? "medical-layout-header" : ""} ${useAdminHeaderStyle ? "admin-layout-header" : ""}`}>
          {/* Hamburger Menu Icon - CLIENT REQUEST: Hidden since WTF sidebar removed from functionality */}
          {/* Client wants ability to restore this later, so commenting out instead of deleting */}
          {/*
          {isWTFRoute && (
            <div
              className="hamburger-row"
              style={{
                display: "flex",
                justifyContent: "flex-start",
                marginBottom: "5px",
              }}
            >
              <button
                className="hamburger-menu"
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                title={
                  isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
                }
              >
                &#9776;
              </button>
            </div>
          )}
          */}

          <div className="user-info" style={{ flexDirection: "row" }}>
            <h2>Hi {localStorage?.getItem("name")}{useMedicalHeaderStyle ? "," : ""}</h2>
            {useMedicalHeaderStyle && (
              <div className="layout-avatar">
                {localStorage?.getItem("name")?.charAt(0)}
              </div>
            )}
          </div>

          {/* Top Menu */}
          {useAdminHeaderStyle ? (
            <AdminTopMenu items={topMenuItems} />
          ) : (
            <div className="top-menu scrollable-menu" ref={topMenuRef}>
              {topMenuItems.map((item) => (
                <div key={item.key} className={item.className} onClick={item.onClick}>
                  {item.label}
                  {item.badge}
                </div>
              ))}
            </div>
          )}

          {localStorage.getItem("role") === "student" && (
            <div
              className="coins"
              onClick={() => navigate('/coins/history')}
              style={{ cursor: 'pointer' }}
              title="View transaction history"
            >
              <span className="coins-label">
                ISF COINS
                <br />
                EARNED
              </span>
              <div
                className="coins-circle"
                data-digits={coinBalance ? coinBalance.toString().length : 0}
              >
                {coinBalance ?? "--"}
              </div>
            </div>
          )}

          {canUseNotificationBell() && (
            <div className="notifications-container">
              {localStorage.getItem("role") === "student" && <CartIcon />}
              <div
                className="notification-bell"
                onClick={handleNotificationClick}
                title={useAdminHeaderStyle ? "Notifications" : undefined}
              >
                {useAdminHeaderStyle ? <Bell size={22} aria-hidden="true" /> : <>&#128276;</>}
                {notifications > 0 && (
                  <span className="notification-badge">{notifications}</span>
                )}
              </div>
              {showNotifications && <NotificationsPanel />}
            </div>
          )}

          {/* Multi-role users only: switch which assigned role is active. */}
          <RoleSwitcher />

          <button className="logout-btn" onClick={logout}>
            {useAdminHeaderStyle && <LogOut size={18} aria-hidden="true" />}
            Logout
          </button>
        </header>
      )}

      <div className="app-container">
        <main className={`main-content ${isShopRoute ? "main-content-shop" : ""} ${isLowStockReportRoute || isDoctorBankRoute ? "main-content-full-page" : ""} ${useMedicalHeaderStyle && shouldShowLayoutMenu ? "main-content-medical-header" : ""} ${location.pathname === "/balagruha" ? "main-content-balagruha" : ""} ${location.pathname === "/attendance" ? "main-content-attendance" : ""} ${location.pathname === "/users" && shouldShowLayoutMenu ? "main-content-users" : ""}`}>
          <SidebarContext.Provider
            value={{
              isSidebarCollapsed,
              toggleSidebar: () => setIsSidebarCollapsed(!isSidebarCollapsed),
            }}
          >
            <Outlet />
          </SidebarContext.Provider>
        </main>
      </div>

      {/* Floating Deliveries Button for Coaches - Sprint5-Story-13 */}
      <FloatingDeliveriesButton />
    </div>
  );
};

export default Layout;


